import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Play, Square, Download, Calendar, User, Timer, ChevronDown } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDuration = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const formatTime = (isoString) => {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (isoString) => {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdminTimeClock = () => {
  const { getAuthHeader, userType } = useAuth();
  const [clockedIn, setClockedIn] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [history, setHistory] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [allHistory, setAllHistory] = useState(null);
  const [period, setPeriod] = useState('week');
  const [viewTab, setViewTab] = useState('my');
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/time-clock/status`, getAuthHeader());
      setClockedIn(res.data.clocked_in);
      setActiveSession(res.data.session);
      if (res.data.clocked_in && res.data.session) {
        const start = new Date(res.data.session.clock_in).getTime();
        setElapsed(Math.floor((Date.now() - start) / 1000));
      }
    } catch (e) { console.error(e); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/time-clock/history?period=${period}`, getAuthHeader());
      setHistory(res.data.sessions);
      setTotalHours(res.data.total_hours);
    } catch (e) { console.error(e); }
  };

  const fetchAllHistory = async () => {
    if (userType !== 'admin') return;
    try {
      const res = await axios.get(`${API}/admin/time-clock/all?period=${period}`, getAuthHeader());
      setAllHistory(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStatus();
      await fetchHistory();
      if (userType === 'admin') await fetchAllHistory();
      setLoading(false);
    };
    init();
  }, [period]);

  // Live timer
  useEffect(() => {
    if (clockedIn && activeSession) {
      timerRef.current = setInterval(() => {
        const start = new Date(activeSession.clock_in).getTime();
        setElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [clockedIn, activeSession]);

  const handleClockIn = async () => {
    try {
      await axios.post(`${API}/admin/time-clock/clock-in`, {}, getAuthHeader());
      toast.success('Clocked in!');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const res = await axios.post(`${API}/admin/time-clock/clock-out`, {}, getAuthHeader());
      toast.success(`Clocked out! Total: ${formatDuration(res.data.total_hours)}`);
      setClockedIn(false);
      setActiveSession(null);
      setElapsed(0);
      if (timerRef.current) clearInterval(timerRef.current);
      await fetchHistory();
      if (userType === 'admin') await fetchAllHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock out');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/time-clock/export?period=${period}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `time_clock_${period}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const elapsedHours = Math.floor(elapsed / 3600);
  const elapsedMins = Math.floor((elapsed % 3600) / 60);
  const elapsedSecs = elapsed % 60;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Big Clock-In Section */}
      <Card className="p-8 border-0 shadow-lg mb-8 bg-gradient-to-br from-slate-900 to-indigo-950 text-white" data-testid="clock-card">
        <div className="flex flex-col items-center text-center">
          {/* Live Timer */}
          <div className="mb-6">
            <p className="text-sm uppercase tracking-wider text-indigo-300 mb-2">
              {clockedIn ? 'Currently Working' : 'Ready to Clock In'}
            </p>
            <div className="font-mono text-6xl md:text-7xl font-bold tracking-tight" data-testid="elapsed-timer">
              {clockedIn ? (
                <span className="text-emerald-400">
                  {String(elapsedHours).padStart(2, '0')}:{String(elapsedMins).padStart(2, '0')}:{String(elapsedSecs).padStart(2, '0')}
                </span>
              ) : (
                <span className="text-slate-500">00:00:00</span>
              )}
            </div>
            {clockedIn && activeSession && (
              <p className="text-sm text-indigo-300 mt-2">
                Since {formatTime(activeSession.clock_in)}
              </p>
            )}
          </div>

          {/* Big Button */}
          {clockedIn ? (
            <Button
              onClick={handleClockOut}
              data-testid="clock-out-btn"
              className="h-20 w-64 text-xl font-bold rounded-2xl bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/40 transition-all hover:scale-105"
            >
              <Square size={24} className="mr-3" />
              Clock Out
            </Button>
          ) : (
            <Button
              onClick={handleClockIn}
              data-testid="clock-in-btn"
              className="h-20 w-64 text-xl font-bold rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/40 transition-all hover:scale-105"
            >
              <Play size={24} className="mr-3" />
              Clock In
            </Button>
          )}
        </div>
      </Card>

      {/* Period Selector & Export */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {['week', 'month', 'year'].map(p => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p)}
              className={period === p ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
              data-testid={`period-${p}-btn`}
            >
              {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'This Year'}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
          <Download size={14} className="mr-2" /> Export CSV
        </Button>
      </div>

      {/* Tabs: My Hours / Team Hours (admin only) */}
      {userType === 'admin' && (
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setViewTab('my')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewTab === 'my' ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-my-hours"
          >
            My Hours
          </button>
          <button
            onClick={() => setViewTab('team')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${viewTab === 'team' ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-team-hours"
          >
            Team Hours
          </button>
        </div>
      )}

      {/* My Hours */}
      {viewTab === 'my' && (
        <>
          <Card className="p-4 border-0 shadow-sm mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Timer size={20} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(totalHours)}</p>
                <p className="text-sm text-gray-500">Total hours this {period}</p>
              </div>
            </div>
          </Card>

          <Card className="border-0 shadow-sm overflow-hidden" data-testid="my-history-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Clock In</th>
                  <th className="px-4 py-3 text-left">Clock Out</th>
                  <th className="px-4 py-3 text-right">Hours</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {history.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No clock sessions this {period}</td></tr>
                )}
                {history.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{formatDate(s.clock_in)}</td>
                    <td className="px-4 py-3">{formatTime(s.clock_in)}</td>
                    <td className="px-4 py-3">{s.clock_out ? formatTime(s.clock_out) : '--'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{s.status === 'completed' ? formatDuration(s.total_hours) : '--'}</td>
                    <td className="px-4 py-3 text-center">
                      {s.status === 'active' ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Completed</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* Team Hours (Admin) */}
      {viewTab === 'team' && allHistory && (
        <>
          {/* User Summaries */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {allHistory.user_summaries.map((u, i) => (
              <Card key={i} className="p-4 border-0 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-sm">
                    {u.user_name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{u.user_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{u.user_role}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-indigo-600">{formatDuration(u.total_hours)}</p>
                    <p className="text-xs text-gray-400">{u.session_count} sessions</p>
                  </div>
                </div>
              </Card>
            ))}
            {allHistory.user_summaries.length === 0 && (
              <p className="text-gray-400 col-span-3 text-center py-8">No team activity this {period}</p>
            )}
          </div>

          {/* All Sessions */}
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="team-history-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Clock In</th>
                  <th className="px-4 py-3 text-left">Clock Out</th>
                  <th className="px-4 py-3 text-right">Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allHistory.sessions.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{s.user_name}</td>
                    <td className="px-4 py-3"><Badge className="bg-gray-100 text-gray-600 capitalize">{s.user_role}</Badge></td>
                    <td className="px-4 py-3">{formatDate(s.clock_in)}</td>
                    <td className="px-4 py-3">{formatTime(s.clock_in)}</td>
                    <td className="px-4 py-3">{s.clock_out ? formatTime(s.clock_out) : '--'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{s.status === 'completed' ? formatDuration(s.total_hours) : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminTimeClock;
